import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Play, Pause, Download, Copy } from "lucide-react";
import { alignToFirst, differenceSignal, differenceWav, binPeaks, matchGains, alignedStart, propertyRows, summaryText, type FileFacts } from "./compareView";
import { fmtSeconds, type CellState } from "./compare";

export interface ViewSide { buffer: AudioBuffer; mono: Float32Array; facts: FileFacts }
interface Props {
  a: ViewSide; b: ViewSide; offsetSeconds: number; cells: CellState[]; identical: boolean;
  headline: string; scorePct: number; offsetLine: string; differsLine: string;
}

const BINS = 600;
const H = 56;

function drawWave(c: HTMLCanvasElement | null, peaks: Float32Array, color: string, scale: number) {
  if (!c) return;
  const dpr = window.devicePixelRatio || 1;
  const w = c.clientWidth || 600;
  c.width = Math.round(w * dpr); c.height = Math.round(H * dpr);
  const g = c.getContext("2d"); if (!g) return;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, H);
  g.fillStyle = color;
  const bw = w / peaks.length;
  for (let i = 0; i < peaks.length; i++) {
    const h = Math.max(peaks[i] > 0 ? 1 : 0, Math.min(1, peaks[i] * scale) * (H / 2));
    g.fillRect(i * bw, H / 2 - h, Math.max(1, bw), h * 2);
  }
}

export function CompareViewPanel({ a, b, offsetSeconds, cells, identical, headline, scorePct, offsetLine, differsLine }: Props) {
  const [match, setMatch] = useState(true);
  const [playing, setPlaying] = useState<"A" | "B" | null>(null);
  const [pos, setPos] = useState(0);
  const ctxRef = useRef<AudioContext | null>(null);
  const srcRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const clockRef = useRef({ startedAt: 0, basePos: 0, delay: 0 });
  const sideRef = useRef<"A" | "B" | null>(null);
  const posRef = useRef(0);
  const rafRef = useRef(0);
  const canvases = { a: useRef<HTMLCanvasElement>(null), b: useRef<HTMLCanvasElement>(null), d: useRef<HTMLCanvasElement>(null) };
  const total = a.mono.length / a.buffer.sampleRate || 1;

  const rows = useMemo(() => propertyRows(a.facts, b.facts), [a, b]);
  const diff = useMemo(() => {
    const rate = a.buffer.sampleRate;
    const bm = b.buffer.sampleRate === rate ? b.mono : resampleLinear(b.mono, b.buffer.sampleRate, rate);
    return { d: differenceSignal(a.mono, bm, offsetSeconds, rate, a.facts.lufs, b.facts.lufs), bAligned: alignToFirst(a.mono, bm, offsetSeconds, rate).b };
  }, [a, b, offsetSeconds]);
  const gains = matchGains(a.facts.lufs, b.facts.lufs, match);

  useEffect(() => {
    const scale = 1 / Math.max(0.05, Math.max(a.facts.peakDb, b.facts.peakDb) > -Infinity ? Math.pow(10, Math.max(a.facts.peakDb, b.facts.peakDb) / 20) : 1);
    drawWave(canvases.a.current, binPeaks(a.mono, BINS), "hsl(215 80% 50%)", scale);
    drawWave(canvases.b.current, binPeaks(diff.bAligned, BINS), "hsl(150 60% 38%)", scale);
    drawWave(canvases.d.current, binPeaks(diff.d.samples, BINS), "hsl(0 70% 50%)", 1 / Math.max(0.05, diff.d.peak));
  }, [a, b, diff]);

  useEffect(() => { if (gainRef.current && sideRef.current) gainRef.current.gain.value = sideRef.current === "A" ? gains.a : gains.b; }, [gains.a, gains.b]);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    try { srcRef.current?.stop(); } catch { /* already stopped */ }
    srcRef.current?.disconnect(); srcRef.current = null; sideRef.current = null; setPlaying(null);
  }, []);
  useEffect(() => () => { stop(); ctxRef.current?.close().catch(() => {}); }, [stop]);

  const start = useCallback((side: "A" | "B", at: number) => {
    cancelAnimationFrame(rafRef.current);
    try { srcRef.current?.stop(); } catch { /* ok */ }
    srcRef.current?.disconnect();
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = (ctxRef.current ??= new AudioCtx());
    void ctx.resume();
    const buf = side === "A" ? a.buffer : b.buffer;
    const { delay, from } = alignedStart(at, offsetSeconds, side);
    if (from >= buf.duration) { stop(); return; }
    const src = ctx.createBufferSource(); src.buffer = buf;
    const g = ctx.createGain(); g.gain.value = side === "A" ? gains.a : gains.b;
    src.connect(g); g.connect(ctx.destination);
    src.start(ctx.currentTime + delay, from);
    src.onended = () => { if (srcRef.current === src && posRef.current >= total - 0.05) { stop(); setPos(0); posRef.current = 0; } };
    srcRef.current = src; gainRef.current = g; sideRef.current = side;
    clockRef.current = { startedAt: ctx.currentTime, basePos: at, delay: 0 };
    setPlaying(side);
    const tick = () => {
      const p = clockRef.current.basePos + (ctx.currentTime - clockRef.current.startedAt);
      posRef.current = p; setPos(Math.min(p, total));
      if (p >= total) { stop(); posRef.current = 0; setPos(0); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [a, b, offsetSeconds, gains.a, gains.b, total, stop]);

  const toggleSide = (side: "A" | "B") => start(side, posRef.current);
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const t = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * total;
    posRef.current = t; setPos(t);
    if (sideRef.current) start(sideRef.current, t);
  };

  const downloadDiff = () => {
    const { bytes, boostDb } = differenceWav(diff.d, a.buffer.sampleRate);
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "audio/wav" }));
    const el = document.createElement("a"); el.href = url; el.download = "audio-difference.wav"; el.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success(boostDb > 0 ? `Difference saved, boosted by ${boostDb} dB so it is audible` : "Difference saved");
  };
  const copySummary = async () => {
    try { await navigator.clipboard.writeText(summaryText(rows, a.facts, b.facts, headline, scorePct, offsetLine, differsLine)); toast.success("Summary copied (text only, no audio)"); }
    catch { toast.error("Couldn't copy; select the table instead."); }
  };

  const pct = (pos / total) * 100;
  return (
    <div className="space-y-5 text-left" data-testid="compare-view">
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Button size="sm" variant={playing === "A" ? "default" : "outline"} onClick={() => toggleSide("A")} aria-pressed={playing === "A"} data-testid="play-a">
            {playing === "A" ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}Play file 1
          </Button>
          <Button size="sm" variant={playing === "B" ? "default" : "outline"} onClick={() => toggleSide("B")} aria-pressed={playing === "B"} data-testid="play-b">
            {playing === "B" ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}Play file 2
          </Button>
          {playing && <Button size="sm" variant="ghost" onClick={stop}>Stop</Button>}
          <label className="flex items-center gap-2 text-sm ml-auto">
            <input type="checkbox" checked={match} onChange={(e) => setMatch(e.target.checked)} disabled={a.facts.lufs === null || b.facts.lufs === null} />
            Match loudness
          </label>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Press the other button while playing to switch files at the same moment{offsetSeconds ? ` (file 2 is lined up by ${Math.abs(offsetSeconds).toFixed(2)} s)` : ""}. Click a waveform to jump.
          {match && a.facts.lufs !== null && b.facts.lufs !== null ? ` Loudness match turns the louder file down by ${Math.abs(Math.round((a.facts.lufs - b.facts.lufs) * 10) / 10)} dB.` : ""}
        </p>
        <div className="relative cursor-pointer select-none" onClick={seek} data-testid="waveforms">
          {[["File 1", canvases.a, "text-blue-600"], ["File 2, lined up with file 1", canvases.b, "text-green-700"], ["Difference (file 1 minus file 2, loudness matched)", canvases.d, "text-red-600"]].map(([label, ref, cls], i) => (
            <div key={i} className="mb-1">
              <p className={`text-[11px] ${cls as string}`}>{label as string}</p>
              <canvas ref={ref as React.RefObject<HTMLCanvasElement>} className="w-full rounded bg-muted/40" style={{ height: H }} role="img"
                aria-label={`${label as string} waveform`} />
            </div>
          ))}
          <div className="absolute top-0 bottom-0 w-px bg-foreground/70 pointer-events-none" style={{ left: `${pct}%` }} aria-hidden />
        </div>
        <div className="flex justify-between text-[11px] text-muted-foreground"><span>0:00</span><span>{fmtSeconds(pos)}</span><span>{fmtSeconds(total)}</span></div>
        <p className="text-xs text-muted-foreground mt-1" data-testid="diff-note">
          {diff.d.bitIdentical ? "After lining up, the audio data of the two files is bit-identical: the difference is silent."
            : diff.d.peak < 0.002 ? "After lining up and matching loudness, the difference is almost silent."
            : "Red shows what is left when file 2 is subtracted from file 1; flat red means they agree there."}
          {b.mono.length / b.buffer.sampleRate - offsetSeconds > total + 0.05 ? " File 2 runs longer than file 1; the extra is not drawn." : ""}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="compare-properties">
          <caption className="sr-only">Properties of the two files</caption>
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2 font-medium">Property</th><th className="pr-2 font-medium">File 1</th><th className="pr-2 font-medium">File 2</th><th className="font-medium">Result</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t">
                <td className="py-1 pr-2">{r.label}</td><td className="pr-2 tabular-nums">{r.a}</td><td className="pr-2 tabular-nums">{r.b}</td>
                <td className={r.same === null ? "text-muted-foreground" : r.same ? "text-green-700" : "text-red-600"} title={r.note}>{r.same === null ? "Unknown" : r.same ? "Same" : "Different"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-1">Browsers resample audio when they decode it, so a sample rate marked "as decoded" is not the file's own. Loudness is integrated LUFS (ITU-R BS.1770); peak is the highest sample, not an inter-sample true peak.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={downloadDiff} data-testid="download-diff"><Download className="h-4 w-4 mr-1" />Download the difference (WAV)</Button>
        <Button size="sm" variant="outline" onClick={copySummary} data-testid="copy-summary"><Copy className="h-4 w-4 mr-1" />Copy result summary</Button>
      </div>
      <p className="text-xs text-muted-foreground">Both stay on this device: the difference file is made in your browser, and the summary is text with file names and numbers only.</p>
    </div>
  );
}

function resampleLinear(x: Float32Array, from: number, to: number): Float32Array {
  const n = Math.floor((x.length * to) / from), out = new Float32Array(n), r = from / to;
  for (let i = 0; i < n; i++) { const p = i * r, k = Math.floor(p), f = p - k; out[i] = x[k] * (1 - f) + (x[k + 1] ?? x[k]) * f; }
  return out;
}
